import { Loader2 } from "lucide-react";
import { STATUS_LABELS } from "@/lib/format";
import { Label } from "@/components/ui/label";

const STATUS_STYLES = {
  requested: "bg-amber-50 text-amber-800 border-amber-200",
  confirmed: "bg-sky-50 text-sky-800 border-sky-200",
  in_progress: "bg-teal-50 text-teal-800 border-teal-200",
  completed: "bg-green-50 text-green-800 border-green-200",
  canceled: "bg-red-50 text-red-800 border-red-200",
  draft: "bg-slate-100 text-slate-700 border-slate-200",
  sent: "bg-sky-50 text-sky-800 border-sky-200",
  approved: "bg-green-50 text-green-800 border-green-200",
  declined: "bg-red-50 text-red-800 border-red-200",
  converted: "bg-teal-50 text-teal-800 border-teal-200",
  unpaid: "bg-amber-50 text-amber-800 border-amber-200",
  partially_paid: "bg-sky-50 text-sky-800 border-sky-200",
  paid: "bg-green-50 text-green-800 border-green-200",
  overdue: "bg-red-50 text-red-800 border-red-200",
  void: "bg-slate-100 text-slate-500 border-slate-200",
  failed: "bg-red-50 text-red-800 border-red-200",
  skipped: "bg-slate-100 text-slate-600 border-slate-200",
  not_sent: "bg-amber-50 text-amber-800 border-amber-200",
};

export function StatusBadge({ status, testId }) {
  return (
    <span data-testid={testId} className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium whitespace-nowrap ${STATUS_STYLES[status] || STATUS_STYLES.draft}`}>
      {STATUS_LABELS[status] || status}
    </span>
  );
}

export function PageHeader({ title, subtitle, actions }) {
  return (
    <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between mb-6 md:mb-8">
      <div>
        <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight" data-testid="page-title">{title}</h1>
        {subtitle && <p className="text-sm text-slate-500 mt-1">{subtitle}</p>}
      </div>
      {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
    </div>
  );
}

export function Panel({ title, action, children, className = "", testId }) {
  return (
    <section data-testid={testId} className={`bg-white border border-slate-200 rounded-xl ${className}`}>
      {title && (
        <div className="flex items-center justify-between px-5 pt-5 pb-3">
          <h2 className="text-base font-semibold">{title}</h2>
          {action}
        </div>
      )}
      <div className={title ? "px-5 pb-5" : "p-5"}>{children}</div>
    </section>
  );
}

export function EmptyState({ icon: Icon, title, text, action, testId }) {
  return (
    <div data-testid={testId} className="flex flex-col items-start gap-2 py-8 px-1">
      {Icon && <div className="h-10 w-10 rounded-lg bg-teal-50 text-teal-700 flex items-center justify-center"><Icon className="h-5 w-5" /></div>}
      <p className="font-medium text-slate-900">{title}</p>
      {text && <p className="text-sm text-slate-500 max-w-md">{text}</p>}
      {action}
    </div>
  );
}

export function Loading({ label = "Loading" }) {
  return (
    <div className="flex items-center gap-2 text-slate-500 py-10" data-testid="loading-indicator">
      <Loader2 className="h-4 w-4 animate-spin" /> {label}…
    </div>
  );
}

export function Field({ label, htmlFor, children, hint, className = "" }) {
  return (
    <div className={`space-y-1.5 ${className}`}>
      <Label htmlFor={htmlFor} className="text-sm font-medium text-slate-700">{label}</Label>
      {children}
      {hint && <p className="text-xs text-slate-500">{hint}</p>}
    </div>
  );
}

export function Stat({ label, value, sub, icon: Icon, testId, tone = "teal" }) {
  const tones = { teal: "bg-teal-50 text-teal-700", navy: "bg-slate-100 text-slate-800", red: "bg-red-50 text-red-700", amber: "bg-amber-50 text-amber-700" };
  return (
    <div data-testid={testId} className="bg-white border border-slate-200 rounded-xl p-5 flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium uppercase tracking-wider text-slate-500">{label}</span>
        {Icon && <span className={`h-8 w-8 rounded-lg flex items-center justify-center ${tones[tone]}`}><Icon className="h-4 w-4" /></span>}
      </div>
      <div className="font-display text-2xl sm:text-3xl font-semibold text-slate-900" data-testid={testId ? `${testId}-value` : undefined}>{value}</div>
      {sub && <div className="text-xs text-slate-500">{sub}</div>}
    </div>
  );
}
